import { BehaviorSubject } from "rxjs";

import { Injectable } from "@angular/core";

import { InspectionImage } from "../_objects/inspection-image";
import {
  AreaLines3D,
  AreaLinesIV7,
  AreaLinesMeasureData_WithReturnCode,
  AreaLinesNX,
  AreaMeasureData_IV7_WithReturnCode,
  AreaMeasureData_NX_WithReturnCode,
  DepthMeasureData_IV7_WithReturnCode,
  DepthMeasureData_NX_WithReturnCode,
  DepthMeasureData_WithReturnCode,
  DistanceMeasureData_IV7_WithReturnCode,
  DistanceMeasureData_NX_WithReturnCode,
  DistanceMeasureData_WithReturnCode,
  LinesMeasureData_IV7_WithReturnCode,
  LinesMeasureData_NX_WithReturnCode,
  MeasurementResultWithReturnCode,
  Model3DData,
  PointSet,
  PointSet_IV7,
  PointSet_IV7_WithReturnCode,
  PointSet_NX,
  PointSet_NX_WithReturnCode,
  PointSetCommonResultWithCode,
  PointSetWithInfo_WithReturnCode,
  PointToLineMeasureData_IV7_WithReturnCode,
  PointToLineMeasureData_NX_WithReturnCode,
  PointToLineMeasureData_WithReturnCode,
  PolylineData_IV7_WithReturnCode,
  PolylineData_NX_WithReturnCode,
  PolylineData_WithReturnCode,
  TipDefectResultData,
  Vertices2Df
} from "../_objects/remeasurement";
import { MEASUREMENT_IMAGE_MODE, MEASUREMENT_IMAGE_TYPE, MEASUREMENT_MODE } from "../app.constants";
import { StereoMeasurementService } from "./http-api/stereo-measurement.service";

@Injectable({
  providedIn: "root"
})
export class MeasurementService {
  private _is3DMeasurement$ = new BehaviorSubject<boolean>(null);
  private _measurementMode$ = new BehaviorSubject<string>(null);
  private _inspectionImage$ = new BehaviorSubject<InspectionImage>(null);

  readonly MAX_RETRY_COUNT = 5;

  constructor(private stereoMeasurementService: StereoMeasurementService) {}

  /**
   * This is the first method to call.
   * @param inspectionImage
   */
  public setInspectionImage(inspectionImage: InspectionImage) {
    this._inspectionImage$.next(inspectionImage);
    this._is3DMeasurement$.next(
      inspectionImage.imageType === MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE || inspectionImage.imageType === MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE
    );
  }

  /**
   * 画像タイプがVEGAの場合は3D計測
   * 画像タイプがSOLEIL, IV7の場合は2D計測を行う
   */
  public get is3DMeasurement() {
    return this._is3DMeasurement$.getValue();
  }

  public get getInspectionImage() {
    return this._inspectionImage$;
  }

  public get getMeasurementType() {
    const imageType = this._inspectionImage$.getValue().imageType;

    switch (imageType) {
      case MEASUREMENT_IMAGE_TYPE.VEGA_IMAGE:
        return MEASUREMENT_IMAGE_MODE.NX;

      case MEASUREMENT_IMAGE_TYPE.IV7_IMAGE:
      case MEASUREMENT_IMAGE_TYPE.SOLEIL_IMAGE:
        return MEASUREMENT_IMAGE_MODE.IV7;

      case MEASUREMENT_IMAGE_TYPE.BRIAN_IMAGE:
        return MEASUREMENT_IMAGE_MODE.ThreeD;

      default:
        throw "Invalid image type in MeasurementService.getMeasurementType";
    }
  }

  /**
   * EXIFの情報から計測タイプを取得
   * "distance" or "point to line" or "depth" or "area" or "lines"
   * @param MeasurementMode
   */
  public setMeasurementModeFromEXIF(MeasurementMode: number) {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      switch (MeasurementMode) {
        case 1: // distance
          this._measurementMode$.next(MEASUREMENT_MODE.DISTANCE);
          break;
        case 2: // point to line
        case 6:
          this._measurementMode$.next(MEASUREMENT_MODE.POINT_TO_LINE);
          break;
        case 3: // depth
          this._measurementMode$.next(MEASUREMENT_MODE.DEPTH);
          break;
        case 4: // area
          this._measurementMode$.next(MEASUREMENT_MODE.AREA);
          break;
        case 5: // lines
          this._measurementMode$.next(MEASUREMENT_MODE.LINES);
          break;
        default:
          break;
      }
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      switch (MeasurementMode) {
        case 1: // distance
          this._measurementMode$.next(MEASUREMENT_MODE.DISTANCE);
          break;
        case 2: // point to line
          this._measurementMode$.next(MEASUREMENT_MODE.POINT_TO_LINE);
          break;
        case 3: // lines
          this._measurementMode$.next(MEASUREMENT_MODE.LINES);
          break;
        case 4: // depth
          this._measurementMode$.next(MEASUREMENT_MODE.DEPTH);
          break;
        case 5: // area
          this._measurementMode$.next(MEASUREMENT_MODE.AREA);
          break;
        default:
          break;
      }
    }
  }

  public get getMeasurementMode(): string {
    return this._measurementMode$.getValue();
  }

  /**
   * 検査画像の計測情報の取得・生成
   * @param inspectionId
   * @param inspectionImageId
   * @param errorNum
   */
  public getAndGenerationOfMeasureInfo(inspectionId: string, inspectionImageId: string, errorNum: number = 0): Promise<any> {
    return this.stereoMeasurementService
      .getAndGenerationOfMeasureInfo(inspectionId, inspectionImageId)
      .toPromise()
      .catch(async (error) => {
        if (errorNum < this.MAX_RETRY_COUNT) {
          return await this.getAndGenerationOfMeasureInfo(inspectionId, inspectionImageId, errorNum + 1);
        }
        console.log(error);
        throw error;
      });
  }

  public async getMissingCornerResult(
    inspectionId: string,
    inspectionImageId: string,
    points: PointSet[],
    model: Model3DData
  ): Promise<MeasurementResultWithReturnCode<TipDefectResultData>> {
    return this.stereoMeasurementService.measureMissingCorner(inspectionId, inspectionImageId, points, model).toPromise();
  }

  /**
   * 【SM02】or【SM07】or【SM16】
   * 画像で打点した箇所の座標データセットを計測, 取得
   */
  public getMeasureDotCoordinateDataSet(
    inspectionId: string,
    inspectionImageId: string,
    data: Vertices2Df,
    model: Model3DData,
    dummyCanvasWidth: number
  ): Promise<PointSetCommonResultWithCode | PointSet_NX_WithReturnCode | PointSet_IV7_WithReturnCode> {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      return this.measureDotCoordinateNXDataSetAsync(inspectionId, inspectionImageId, data, dummyCanvasWidth);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      return this.measureDotCoordinateIV7DataSetAsync(inspectionId, inspectionImageId, data, dummyCanvasWidth);
    } else {
      return this.measureDotCoordinate3dDataSetAsync(inspectionId, inspectionImageId, data, model, dummyCanvasWidth);
    }
  }

  /**
   * 【SM03】or【SM08】or【SM17】歪再現曲線の構成点を取得
   */
  public async getDistortionReproductionCurveAsync(
    inspectionId: string,
    inspectionImageId: string,
    leftPoints2d: Vertices2Df[],
    rightPoints2d: Vertices2Df[],
    lineType: number,
    errorNum: number = 0
  ): Promise<Array<PolylineData_NX_WithReturnCode | PolylineData_IV7_WithReturnCode | PolylineData_WithReturnCode>> {
    let leftLineCoordinateTask: Promise<PolylineData_NX_WithReturnCode | PolylineData_IV7_WithReturnCode | PolylineData_WithReturnCode>;
    let rightLineCoordinateTask: Promise<PolylineData_NX_WithReturnCode | PolylineData_IV7_WithReturnCode | PolylineData_WithReturnCode>;

    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      leftLineCoordinateTask = this.getDistortionReproductionCurveNXAsync(inspectionId, inspectionImageId, leftPoints2d, lineType, 0);
      rightLineCoordinateTask = this.getDistortionReproductionCurveNXAsync(inspectionId, inspectionImageId, rightPoints2d, lineType, 1);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      leftLineCoordinateTask = this.getDistortionReproductionCurveIV7Async(inspectionId, inspectionImageId, leftPoints2d, lineType, 0);
      rightLineCoordinateTask = this.getDistortionReproductionCurveIV7Async(inspectionId, inspectionImageId, rightPoints2d, lineType, 1);
    } else {
      leftLineCoordinateTask = this.getDistortionReproductionCurve3dAsync(inspectionId, inspectionImageId, leftPoints2d, lineType, 0);
      rightLineCoordinateTask = this.getDistortionReproductionCurve3dAsync(inspectionId, inspectionImageId, rightPoints2d, lineType, 1);
    }

    const task = Promise.all([leftLineCoordinateTask, rightLineCoordinateTask]);

    return task.catch(async (error) => {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getDistortionReproductionCurveAsync(inspectionId, inspectionImageId, leftPoints2d, rightPoints2d, lineType, errorNum + 1);
      }
      console.log(error);
      throw error;
    });
  }

  public async getTwoPointsMeasure(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: PointSet[] | PointSet_IV7[] | PointSet_NX[]
  ): Promise<DistanceMeasureData_WithReturnCode | DistanceMeasureData_IV7_WithReturnCode | DistanceMeasureData_NX_WithReturnCode> {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      pointSets = pointSets as PointSet_NX[];
      return this.getTwoPointMeasureNX(inspectionId, inspectionImageId, pointSets);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      pointSets = pointSets as PointSet_IV7[];
      return this.getTwoPointMeasureIV7(inspectionId, inspectionImageId, pointSets);
    } else {
      pointSets = pointSets as PointSet[];
      return this.getTwoPointMeasure3d(inspectionId, inspectionImageId, pointSets);
    }
  }

  /**
   * 【SM05】or【SM10】or【SM19】線基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @returns
   */
  public async getLineReferenceMeasure(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet[] | PointSet_IV7[] | PointSet_NX[]
  ): Promise<PointToLineMeasureData_WithReturnCode | PointToLineMeasureData_IV7_WithReturnCode | PointToLineMeasureData_NX_WithReturnCode> {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      pointList = pointList as PointSet_NX[];
      return this.getLineReferenceMeasureNX(inspectionId, inspectionImageId, pointList);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      pointList = pointList as PointSet_IV7[];
      return this.getLineReferenceMeasureIV7(inspectionId, inspectionImageId, pointList);
    } else {
      pointList = pointList as PointSet[];
      return this.getLineReferenceMeasure3d(inspectionId, inspectionImageId, pointList);
    }
  }

  /**
   * 【SM06】or【SM11】or【SM20】面基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @param errorNum
   * @returns
   */
  public async getSurfaceReferenceMeasure(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet[] | PointSet_IV7[] | PointSet_NX[],
    model: Model3DData
  ): Promise<DepthMeasureData_WithReturnCode | DepthMeasureData_IV7_WithReturnCode | DepthMeasureData_NX_WithReturnCode> {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      pointList = pointList as PointSet_NX[];
      return this.getSurfaceReferenceMeasureNX(inspectionId, inspectionImageId, pointList);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      pointList = pointList as PointSet_IV7[];
      return this.getSurfaceReferenceMeasureIV7(inspectionId, inspectionImageId, pointList);
    } else {
      pointList = pointList as PointSet[];
      return this.getSurfaceReferenceMeasure3d(inspectionId, inspectionImageId, pointList, model);
    }
  }

  public async getAreaMeasure(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: AreaLinesIV7 | AreaLinesNX
  ): Promise<AreaMeasureData_IV7_WithReturnCode | AreaMeasureData_NX_WithReturnCode> {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      const pointSets_NX = pointSets as AreaLinesNX;
      pointSets_NX.pntCount = pointSets_NX.pntCount - 1;
      pointSets_NX.pointSets = pointSets_NX.pointSets.filter((_, index) => index !== pointSets_NX.pntCount);
      return this.getAreaMeasureNX(inspectionId, inspectionImageId, pointSets_NX);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      pointSets = pointSets as AreaLinesIV7;
      return this.getAreaMeasureIV7(inspectionId, inspectionImageId, pointSets);
    }
  }

  public async getOverallLengthMeasure(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: AreaLinesIV7 | AreaLinesNX | AreaLines3D
  ): Promise<LinesMeasureData_IV7_WithReturnCode | LinesMeasureData_NX_WithReturnCode | AreaLinesMeasureData_WithReturnCode> {
    if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.NX) {
      pointSets = pointSets as AreaLinesNX;
      return this.getOverallLengthMeasureNX(inspectionId, inspectionImageId, pointSets);
    } else if (this.getMeasurementType === MEASUREMENT_IMAGE_MODE.IV7) {
      pointSets = pointSets as AreaLinesIV7;
      return this.getOverallLengthMeasureIV7(inspectionId, inspectionImageId, pointSets);
    } else {
      pointSets = pointSets as AreaLines3D;
      return this.getAreaTotalLengthMeasure(inspectionId, inspectionImageId, pointSets);
    }
  }

  /**
   * 面積全長計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointSets
   * @param errorNum
   * @returns
   */
  public async getAreaTotalLengthMeasure(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: AreaLines3D,
    errorNum: number = 0
  ): Promise<AreaLinesMeasureData_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getAreaTotalLengthMeasure(inspectionId, inspectionImageId, pointSets).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getAreaTotalLengthMeasure(inspectionId, inspectionImageId, pointSets, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /** 3Dオブジェクト表示に必要なデータの取得 */
  public async get3DModelData(inspectionId: string, inspectionImageId: string, errorNum: number = 0): Promise<Model3DData> {
    try {
      const modelData = await this.stereoMeasurementService.get3DModelData(inspectionId, inspectionImageId).toPromise();
      if (modelData.mesh_count == 0 && modelData.point_count == 0) {
        return await this.get3DModelData(inspectionId, inspectionImageId, errorNum);
      }
      return modelData;
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.get3DModelData(inspectionId, inspectionImageId, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 3Dオブジェクトのテクスチャ画像が配置されているPathを取得
   * @param inspectionId
   * @param inspectionImageId
   * @param errorNum
   * @returns
   */
  public async getImageBinaryUrl(inspectionId: string, inspectionImageId: string, errorNum: number = 0): Promise<string> {
    return this.stereoMeasurementService.getImageBinaryUrl(inspectionId, inspectionImageId).catch(async (err) => {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getImageBinaryUrl(inspectionId, inspectionImageId, errorNum + 1);
      }
      console.log(err);
      throw err;
    });
  }

  private async measureDotCoordinate3dDataSetAsync(
    inspectionId: string,
    inspectionImageId: string,
    data: Vertices2Df,
    model: Model3DData,
    dummyCanvasWidth: number,
    errorNum: number = 0
  ): Promise<PointSetCommonResultWithCode> {
    try {
      // IV7と共通化する為に、PointSetWithInfo_WithReturnCodeで受け取り、PointSetCommonResultWithCodeの型に成型
      const pntsetWithInfo: PointSetWithInfo_WithReturnCode = await this.stereoMeasurementService.measureDotCoordinate3dDataSetAsync(
        inspectionId,
        inspectionImageId,
        data,
        model
      );
      const result: PointSetCommonResultWithCode = new PointSetCommonResultWithCode();

      result.pointSetResult.msr_pnt = pntsetWithInfo.pointSetResult.pntset.msr_pnt;
      result.pointSetResult.res_pnt = pntsetWithInfo.pointSetResult.pntset.res_pnt;
      result.pointSetResult.msr_pnt_cor = pntsetWithInfo.pointSetResult.pntset.msr_pnt_cor;
      result.pointSetResult.res_pnt_cor = pntsetWithInfo.pointSetResult.pntset.res_pnt_cor;
      result.pointSetResult.pnt_3d = pntsetWithInfo.pointSetResult.pntset.pnt_3d;
      result.pointSetResult.isMatch = pntsetWithInfo.pointSetResult.flgs === 1;
      result.returnCode = pntsetWithInfo.returnCode;

      return result;
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.measureDotCoordinate3dDataSetAsync(inspectionId, inspectionImageId, data, model, dummyCanvasWidth, errorNum + 1);
      } else {
        console.log(error);
        throw error;
      }
    }
  }

  private async getDistortionReproductionCurve3dAsync(
    inspectionId: string,
    inspectionImageId: string,
    points2d: Vertices2Df[],
    lineType: number,
    lr: number
  ): Promise<PolylineData_WithReturnCode> {
    return this.stereoMeasurementService.getDistortionReproductionCurve3dAsync(inspectionId, inspectionImageId, points2d, lineType, lr);
  }

  private async getTwoPointMeasure3d(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: PointSet[],
    errorNum: number = 0
  ): Promise<DistanceMeasureData_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getTwoPointMeasure3d(inspectionId, inspectionImageId, pointSets).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getTwoPointMeasure3d(inspectionId, inspectionImageId, pointSets, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 線基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @returns
   */
  private async getLineReferenceMeasure3d(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet[],
    errorNum: number = 0
  ): Promise<PointToLineMeasureData_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getLineReferenceMeasure3d(inspectionId, inspectionImageId, pointList).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getLineReferenceMeasure3d(inspectionId, inspectionImageId, pointList, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  private async getSurfaceReferenceMeasure3d(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet[],
    model: Model3DData,
    errorNum: number = 0
  ): Promise<DepthMeasureData_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getSurfaceReferenceMeasure3d(inspectionId, inspectionImageId, pointList, model).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getSurfaceReferenceMeasure3d(inspectionId, inspectionImageId, pointList, model, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 2D画像で打点した箇所の座標データセットを計測
   *
   * @param inspectionId
   * @param inspectionImageId
   * @param data
   * @param dummyCanvasWidth
   * @param errorNum
   * @returns
   */
  private async measureDotCoordinateIV7DataSetAsync(
    inspectionId: string,
    inspectionImageId: string,
    data: Vertices2Df,
    dummyCanvasWidth: number,
    errorNum: number = 0
  ): Promise<PointSet_IV7_WithReturnCode> {
    try {
      const pntset: PointSet_IV7_WithReturnCode = await this.stereoMeasurementService.measureDotCoordinateIV7DataSetAsync(
        inspectionId,
        inspectionImageId,
        data
      );
      const result: PointSet_IV7_WithReturnCode = new PointSet_IV7_WithReturnCode();
      const isMatch: boolean = !(
        pntset.pointSetResult.fmatch[0] === 0 &&
        pntset.pointSetResult.fmatch[1] === 0 &&
        pntset.pointSetResult.fmatch[2] === 0
      );

      result.pointSetResult.msr_pnt = pntset.pointSetResult.msr_pnt;
      result.pointSetResult.res_pnt = pntset.pointSetResult.res_pnt;
      result.pointSetResult.pnt_3d = pntset.pointSetResult.pnt_3d;
      result.pointSetResult.fmatch = pntset.pointSetResult.fmatch;
      result.pointSetResult.isMatch = isMatch;
      result.returnCode = pntset.returnCode;

      return result;
    } catch (error) {
      console.log(error);
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.measureDotCoordinateIV7DataSetAsync(inspectionId, inspectionImageId, data, dummyCanvasWidth, errorNum + 1);
      } else {
        console.log(error);
        throw error;
      }
    }
  }

  /**
   * @param inspectionId
   * @param inspectionImageId
   * @param points2d
   * @param lineType
   * @param lr
   * @param errorNum
   * @returns
   */
  private async getDistortionReproductionCurveIV7Async(
    inspectionId: string,
    inspectionImageId: string,
    points2d: Vertices2Df[],
    lineType: number,
    lr: number
  ): Promise<PolylineData_IV7_WithReturnCode> {
    return this.stereoMeasurementService.getDistortionReproductionCurveIV7Async(inspectionId, inspectionImageId, points2d, lineType, lr);
  }

  /**
   * 二点間計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointSets
   * @returns
   */
  private async getTwoPointMeasureIV7(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: PointSet_IV7[],
    errorNum: number = 0
  ): Promise<DistanceMeasureData_IV7_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getTwoPointMeasureIV7(inspectionId, inspectionImageId, pointSets).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getTwoPointMeasureIV7(inspectionId, inspectionImageId, pointSets, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 線基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @returns
   */
  private async getLineReferenceMeasureIV7(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet_IV7[],
    errorNum: number = 0
  ): Promise<PointToLineMeasureData_IV7_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getLineReferenceMeasureIV7(inspectionId, inspectionImageId, pointList).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getLineReferenceMeasureIV7(inspectionId, inspectionImageId, pointList, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 面基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @param errorNum
   */
  private async getSurfaceReferenceMeasureIV7(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet_IV7[],
    errorNum: number = 0
  ): Promise<DepthMeasureData_IV7_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getSurfaceReferenceMeasureIV7(inspectionId, inspectionImageId, pointList).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getSurfaceReferenceMeasureIV7(inspectionId, inspectionImageId, pointList, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 面積計測結果を取得
   */
  public async getAreaMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7, errorNum: number = 0) {
    try {
      return this.stereoMeasurementService.getAreaMeasure(inspectionId, inspectionImageId, pointSets).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getAreaMeasureIV7(inspectionId, inspectionImageId, pointSets, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 全長計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointSets
   * @param errorNum
   * @returns
   */
  public async getOverallLengthMeasureIV7(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesIV7, errorNum: number = 0) {
    return this.stereoMeasurementService
      .getOverallLengthMeasure(inspectionId, inspectionImageId, pointSets)
      .toPromise()
      .catch(async (err) => {
        if (errorNum < this.MAX_RETRY_COUNT) {
          return await this.getOverallLengthMeasureIV7(inspectionId, inspectionImageId, pointSets, errorNum + 1);
        }
        console.log(err);
        throw err;
      });
  }

  /**
   * 2D画像で打点した箇所の座標データセットを計測
   *
   * @param inspectionId
   * @param inspectionImageId
   * @param data
   * @param dummyCanvasWidth
   * @param errorNum
   * @returns
   */
  private async measureDotCoordinateNXDataSetAsync(
    inspectionId: string,
    inspectionImageId: string,
    data: Vertices2Df,
    dummyCanvasWidth: number,
    errorNum: number = 0
  ): Promise<PointSet_NX_WithReturnCode> {
    try {
      const pntset: PointSet_NX_WithReturnCode = await this.stereoMeasurementService.measureDotCoordinateNXDataSetAsync(
        inspectionId,
        inspectionImageId,
        data
      );
      const result: PointSet_NX_WithReturnCode = new PointSet_NX_WithReturnCode();
      const isMatch: boolean = pntset.pointSetResult.acc_val === 0;

      result.pointSetResult.msr_pnt = pntset.pointSetResult.msr_pnt;
      result.pointSetResult.res_pnt = pntset.pointSetResult.res_pnt;
      result.pointSetResult.msr_pnt_cor = pntset.pointSetResult.msr_pnt_cor;
      result.pointSetResult.res_pnt_cor = pntset.pointSetResult.res_pnt_cor;
      result.pointSetResult.pnt_3d = pntset.pointSetResult.pnt_3d;
      result.pointSetResult.acc_val = pntset.pointSetResult.acc_val;
      result.pointSetResult.isMatch = isMatch;
      result.returnCode = pntset.returnCode;

      return result;
    } catch (error) {
      console.log(error);
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.measureDotCoordinateNXDataSetAsync(inspectionId, inspectionImageId, data, dummyCanvasWidth, errorNum + 1);
      } else {
        console.log(error);
        throw error;
      }
    }
  }

  /**
   * @param inspectionId
   * @param inspectionImageId
   * @param points2d
   * @param lineType
   * @param lr
   * @param errorNum
   * @returns
   */
  private async getDistortionReproductionCurveNXAsync(
    inspectionId: string,
    inspectionImageId: string,
    points2d: Vertices2Df[],
    lineType: number,
    lr: number
  ): Promise<PolylineData_NX_WithReturnCode> {
    return this.stereoMeasurementService.getDistortionReproductionCurveNXAsync(inspectionId, inspectionImageId, points2d, lineType, lr);
  }

  /**
   * 二点間計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointSets
   * @returns
   */
  private async getTwoPointMeasureNX(
    inspectionId: string,
    inspectionImageId: string,
    pointSets: PointSet_NX[],
    errorNum: number = 0
  ): Promise<DistanceMeasureData_NX_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getTwoPointMeasureNX(inspectionId, inspectionImageId, pointSets).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getTwoPointMeasureNX(inspectionId, inspectionImageId, pointSets, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 線基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @returns
   */
  private async getLineReferenceMeasureNX(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet_NX[],
    errorNum: number = 0
  ): Promise<PointToLineMeasureData_NX_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getLineReferenceMeasureNX(inspectionId, inspectionImageId, pointList).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getLineReferenceMeasureNX(inspectionId, inspectionImageId, pointList, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 面基準計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointList
   * @param errorNum
   */
  private async getSurfaceReferenceMeasureNX(
    inspectionId: string,
    inspectionImageId: string,
    pointList: PointSet_NX[],
    errorNum: number = 0
  ): Promise<DepthMeasureData_NX_WithReturnCode> {
    try {
      return this.stereoMeasurementService.getSurfaceReferenceMeasureNX(inspectionId, inspectionImageId, pointList).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getSurfaceReferenceMeasureNX(inspectionId, inspectionImageId, pointList, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 面積計測結果を取得
   */
  public async getAreaMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX, errorNum: number = 0) {
    try {
      return this.stereoMeasurementService.getAreaMeasureNX(inspectionId, inspectionImageId, pointSets).toPromise();
    } catch (error) {
      if (errorNum < this.MAX_RETRY_COUNT) {
        return await this.getAreaMeasureNX(inspectionId, inspectionImageId, pointSets, errorNum + 1);
      }
      console.log(error);
      throw error;
    }
  }

  /**
   * 全長計測結果を取得
   * @param inspectionId
   * @param inspectionImageId
   * @param pointSets
   * @param errorNum
   * @returns
   */
  public async getOverallLengthMeasureNX(inspectionId: string, inspectionImageId: string, pointSets: AreaLinesNX, errorNum: number = 0) {
    return this.stereoMeasurementService
      .getOverallLengthMeasureNX(inspectionId, inspectionImageId, pointSets)
      .toPromise()
      .catch(async (err) => {
        if (errorNum < this.MAX_RETRY_COUNT) {
          return await this.getOverallLengthMeasureNX(inspectionId, inspectionImageId, pointSets, errorNum + 1);
        }
        console.log(err);
        throw err;
      });
  }
}
